import type { VoidFunctionComponent } from 'react';
import { ControlLabel, FormGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { CANNOT_DISABLE_NON_CUSTOM_TEAM_ROLES_ERROR } from '../../../../../shared/organizationPackages/errors';
import type { GeneralSettings } from '../../../../../shared/settings/general';
import { orgRolesList, roleLabel } from '../../../../helpers/role';
import { useDispatchOnMount } from '../../../../hooks';
import RolesModules from '../../../../redux/roles';
import { AccessGate, ErrorAlert } from '../../../generic';
import DropdownButton from '../../../generic/DropdownButton';
import Flex from '../../../generic/Flex';
import { icons } from '../../../generic/Icon';
import Text from '../../../generic/Text';
import Toggle from '../../../generic/Toggle';
import TooltipIcon from '../../../generic/TooltipIcon';

type Props = {
  settings: GeneralSettings;
  onUpdateSettings: (settings: Partial<GeneralSettings>) => void;
  isDisabled: boolean;
  errors: string[] | null;
};

/**
 * Allow users to set the default role for new users.
 * This is only available to premium organizations.
 */
const UsersSection: VoidFunctionComponent<Props> = ({ settings, isDisabled, onUpdateSettings, errors }) => {
  const roles = useSelector(RolesModules.selectors.getRoles);

  useDispatchOnMount(RolesModules.actions.fetchAll);

  return (
    <AccessGate level="premium" behavior="hide">
      {errors?.some((error) => error.includes(CANNOT_DISABLE_NON_CUSTOM_TEAM_ROLES_ERROR)) && (
        <ErrorAlert status={{ errors }} />
      )}
      <Text.H3>Users</Text.H3>
      <div className="panel padded">
        <FormGroup className="default-role">
          <Flex justify="space-between">
            <ControlLabel>Default Role for New Users</ControlLabel>
            <DropdownButton
              title={roleLabel(roles, settings.defaultRoleId || null)}
              disabled={isDisabled}
              id="org-default-role"
              bsStyle="button"
            >
              {orgRolesList(roles).map((role) => (
                <DropdownButton.Item
                  key={role.id}
                  onSelect={() => {
                    onUpdateSettings({
                      defaultRoleId: role.id,
                    });
                  }}
                  eventKey={role.id}
                  active={settings?.defaultRoleId === role.id}
                  name="defaultRoleId"
                >
                  {role.name}
                </DropdownButton.Item>
              ))}
            </DropdownButton>
          </Flex>
        </FormGroup>
        <FormGroup>
          <Flex justify="space-between">
            <ControlLabel>
              Disable FOSSA default team roles{' '}
              <TooltipIcon
                id="disable-non-custom-team-user-roles-tooltip"
                tooltip="Enabling this will hide the built in roles that FOSSA offers out of the box."
                icon={icons.infoCircle}
              />
            </ControlLabel>
            <Toggle
              checked={settings.disableNonCustomTeamUserRoles}
              isDisabled={isDisabled}
              onChange={(isChecked) => {
                onUpdateSettings({
                  disableNonCustomTeamUserRoles: isChecked,
                });
              }}
            />
          </Flex>
        </FormGroup>
      </div>
    </AccessGate>
  );
};

export default UsersSection;
